// Purchase orders: rules shared by the page (src/pages/PurchaseOrders.jsx), the server (api/_po.js) and the AI (api/_agent.js).
// Plain JS, no imports - the tests run it in node. The database triggers (sql/purchase-orders.sql) enforce the same flow.

export const PO_STATUSES = ['draft', 'rfq', 'approved', 'sent', 'confirmed', 'partially_delivered', 'delivered', 'cancelled']
export const PO_LABEL = { draft: 'Draft', rfq: 'Quotes requested', approved: 'Approved', sent: 'Sent', confirmed: 'Confirmed', partially_delivered: 'Part delivered', delivered: 'Delivered', cancelled: 'Cancelled' }
export const PO_TONE = { draft: 'grey', rfq: 'amber', approved: 'blue', sent: 'amber', confirmed: 'amber', partially_delivered: 'amber', delivered: 'green', cancelled: 'red' }
export const OPEN_STATUSES = ['draft', 'rfq', 'approved', 'sent', 'confirmed', 'partially_delivered']
export const SUPPLIER_CATEGORIES = ['chandler', 'engines', 'electrical', 'electronics', 'safety', 'fuel', 'provisions', 'interior', 'other']

// same list as po_transition_ok() in the SQL
const NEXT = {
  draft: ['approved', 'cancelled', 'rfq'],
  rfq: ['draft', 'cancelled'],
  approved: ['sent', 'draft', 'cancelled'],
  sent: ['confirmed', 'partially_delivered', 'delivered', 'cancelled'],
  confirmed: ['partially_delivered', 'delivered', 'cancelled'],
  partially_delivered: ['delivered'],
  delivered: [],
  cancelled: [],
}
export const canTransition = (from, to) => (NEXT[from] || []).includes(to)

// permission level (0 none, 1 view, 2 edit, 3 full) needed for a status change
export function levelFor(from, to) {
  if (to === 'approved') return 3
  if (to === 'cancelled' && from !== 'draft' && from !== 'rfq') return 3
  return 2
}

const num = v => (v === '' || v == null || !Number.isFinite(Number(v)) ? null : Number(v))
export const unitPrice = l => num(l.confirmed_price) ?? num(l.unit_price)
export function lineTotal(l) { const p = unitPrice(l), q = num(l.qty); return p != null && q != null ? Math.round(p * q * 100) / 100 : null }
export function poTotal(lines) {
  const priced = (lines || []).filter(l => unitPrice(l) != null)
  if (!priced.length) return null
  return Math.round(priced.reduce((s, l) => s + lineTotal(l), 0) * 100) / 100
}
export const fmtMoney = (v, cur) => (v == null ? '' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (cur ? ' ' + cur : ''))
export const fmtQty = v => (v == null ? '' : String(Math.round(Number(v) * 1000) / 1000))

// one plain-text order for WhatsApp / SMS (nothing is sent - the user copies it)
export function whatsappText(po, lines, vessel) {
  const v = vessel || {}
  const out = []
  out.push('PURCHASE ORDER ' + (po.po_number || '(draft)'))
  out.push((v.vessel_name || 'Vessel') + (po.supplier_name ? ' → ' + po.supplier_name : ''))
  out.push('')
  ;(lines || []).forEach((l, i) => {
    out.push((i + 1) + '. ' + fmtQty(l.qty) + ' ' + (l.unit || 'pcs') + ' × ' + l.description + (l.part_number ? ' (P/N ' + l.part_number + ')' : ''))
  })
  out.push('')
  if (po.delivery_port) out.push('Deliver to: ' + po.delivery_port)
  if (po.needed_by) out.push('Needed by: ' + po.needed_by)
  if (po.notes) out.push('Notes: ' + po.notes)
  if (v.billing_name || v.billing_address || v.vat_number) {
    out.push('Invoice to: ' + [v.billing_name, v.billing_address && String(v.billing_address).replace(/\s*\n\s*/g, ', '), v.vat_number && 'VAT ' + v.vat_number].filter(Boolean).join(', '))
  }
  if (po.approved_by_name) out.push('Approved by: ' + po.approved_by_name)
  out.push('')
  out.push('Please confirm price and delivery date, quoting ' + (po.po_number || 'this order') + '.')
  return out.join('\n')
}
// wa.me link (opens WhatsApp with the text; the user still presses send)
export function whatsappLink(phone, text) {
  const d = String(phone || '').replace(/[^0-9]/g, '').replace(/^00/, '')
  return d.length >= 7 ? 'https://wa.me/' + d + '?text=' + encodeURIComponent(text) : null
}

// which inventory item a delivered line goes to: the linked one, else exactly ONE item with the same part number.
// returns { item } or { candidates } (0 or several -> the user picks / creates one: "Add to stock?")
const pn = v => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '')
export function matchInventory(line, inventory) {
  const inv = inventory || []
  if (line.inventory_id) { const it = inv.find(i => i.id === line.inventory_id); if (it) return { item: it } }
  if (pn(line.part_number)) {
    const hits = inv.filter(i => pn(i.part_number) && pn(i.part_number) === pn(line.part_number))
    if (hits.length === 1) return { item: hits[0] }
    if (hits.length > 1) return { candidates: hits }
  }
  return { candidates: [] }
}

export const stockStatus = (q, min) => (q <= 0 ? 'Out of Stock' : (min != null && min !== '' && q <= Number(min) ? 'Low Stock' : 'OK'))

// receiving: received = { lineId: qty received NOW } -> what to write.
// Each line: new qty_received, how much goes into stock (delta not yet posted) and how much money to the budget.
export function receivePlan(lines, received) {
  const out = []
  for (const l of lines || []) {
    const now = num((received || {})[l.id])
    if (!now || now <= 0) continue
    const before = num(l.qty_received) || 0
    const total = before + now
    const p = unitPrice(l)
    out.push({ id: l.id, line: l, now, qty_received: total, stock_delta: total - (num(l.stock_posted) || 0), cost_delta: p != null ? Math.round(p * now * 100) / 100 : 0, over: total > Number(l.qty) })
  }
  return out
}
// status the database will set after receiving (for messages in the page)
export function deliveryStatus(status, lines) {
  if (!['sent', 'confirmed', 'partially_delivered'].includes(status)) return status
  const all = (lines || []).length && lines.every(l => (num(l.qty_received) || 0) >= Number(l.qty))
  if (all) return 'delivered'
  if ((lines || []).some(l => (num(l.qty_received) || 0) > 0)) return 'partially_delivered'
  return status
}

// a short answer for "where are my filters?": each open line with its PO state
export function whereIs(po, lines) {
  const st = po.status
  const eta = po.confirmed_delivery_date ? 'confirmed for ' + po.confirmed_delivery_date : po.needed_by ? 'needed by ' + po.needed_by : ''
  return (lines || []).map(l => {
    const rec = num(l.qty_received) || 0
    const state = st === 'delivered' || rec >= Number(l.qty) ? 'received on board' : rec > 0 ? rec + ' of ' + fmtQty(l.qty) + ' received' : { draft: 'not ordered yet (draft, waiting for approval)', rfq: 'not ordered yet (asking suppliers for quotes)', approved: 'approved, not sent to the supplier yet', sent: 'ordered, waiting for the supplier to confirm', confirmed: 'ordered and confirmed by the supplier', partially_delivered: 'ordered, not delivered yet', cancelled: 'order cancelled' }[st] || st
    return { po: po.po_number, supplier: po.supplier_name, item: l.description, part_number: l.part_number || null, qty: Number(l.qty), unit: l.unit, received: rec, state, eta: st === 'cancelled' || st === 'delivered' ? '' : eta, delivery_port: po.delivery_port || null }
  })
}

// ---------------------------------------------------------------------------
// Level 2: approval by amount, quotes, wishlist. The database (sql/po-level2.sql) decides; these mirror it for the page and the AI.
// ---------------------------------------------------------------------------
export const DEFAULT_PO_SETTINGS = { currency: 'EUR', tier1_max: 500, tier2_max: 5000, rates: { USD: 0.92, GBP: 1.17 } }
export const SLOT_LABEL = { full: 'Any approver with Full (HOD, chief or Captain)', captain_or_management: 'Captain or management', captain: 'Captain', management: 'Management' }
export const TIER_LABEL = { 1: 'up to the first limit', 2: 'between the limits', 3: 'over the second limit', no_price: 'items without a price', no_rate: 'no exchange rate for this currency' }
export const WISH_DEPARTMENTS = ['deck', 'engine', 'interior', 'galley', 'bridge', 'other']
export const WISH_PRIORITIES = ['urgent', 'high', 'normal', 'low']

// same rule as po_rule() in the SQL: { tier, slots, amount } - amount in the settings currency
export function approvalRule({ total, currency, lines, settings, hasCompany }) {
  const s = { ...DEFAULT_PO_SETTINGS, ...(settings || {}) }
  const unpriced = total == null || (lines || []).some(l => unitPrice(l) == null)
  const rate = String(currency || s.currency).toUpperCase() === String(s.currency).toUpperCase() ? 1 : num((s.rates || {})[String(currency).toUpperCase()])
  let tier, slots, amount = null
  if (rate == null || rate <= 0) { tier = 'no_rate'; slots = ['captain', 'management'] }
  else {
    amount = Math.round((Number(total) || 0) * rate * 100) / 100
    if (amount > Number(s.tier2_max)) { tier = '3'; slots = ['captain', 'management'] }
    else if (unpriced) { tier = 'no_price'; slots = ['captain'] }
    else if (amount > Number(s.tier1_max)) { tier = '2'; slots = ['captain_or_management'] }
    else { tier = '1'; slots = ['full'] }
  }
  if (!hasCompany && !(slots.length === 1 && slots[0] === 'full')) slots = ['captain']
  return { tier, slots, amount: unpriced && total == null ? null : amount }
}
export const slotOk = (slot, roles) => {
  const r = roles || []
  if (slot === 'full') return r.some(x => ['full', 'captain'].includes(x))   // the on-board tier: management is not one of them (sql/test-fixes-7.sql)
  if (slot === 'captain_or_management') return r.includes('captain') || r.includes('management')
  return r.includes(slot)
}
// what is still missing on an order, from the order's columns and its approval rows
export function approvalState(po, approvals) {
  const slots = po.approval_slots || []
  const done = (approvals || []).map(a => a.slot)
  const missing = slots.filter(s => !done.includes(s))
  const required = po.approvals_required ?? slots.length
  const count = po.status === 'approved' ? required : (po.approvals_count ?? done.length)
  return { required, count, missing, text: required > 1 || count ? count + ' of ' + required + ' approved' : '' }
}
// can this login give one of the missing approvals? (not if they already approved)
export function canApproveNow(po, approvals, roles, uid) {
  if (po.status !== 'draft') return false
  if ((approvals || []).some(a => a.approver === uid)) return false
  return approvalState(po, approvals).missing.some(s => slotOk(s, roles))
}
// one plain statement per order for the AI to repeat, straight from approval_slots / approvals_count:
// "HC-2026-0001 (141.00 EUR): 0 of 1 approvals, needs: one approval from anyone with Full (...), you can approve: yes"
const NEED_TEXT = { full: 'one approval from anyone with Full on Purchase Orders (HOD, chief or Captain - any one of them; not management)', captain_or_management: 'one approval from the Captain or management (either one)', captain: 'the Captain', management: 'management' }
export function approvalLine(po, approvals, roles, uid) {
  const st = approvalState(po, approvals)
  const can = canApproveNow(po, approvals, roles, uid)
  const already = (approvals || []).some(a => a.approver === uid)
  const needs = st.missing.map(x => NEED_TEXT[x] || x)
  const total = po.total != null ? fmtMoney(po.total, po.currency) : 'no prices'
  const line = po.po_number + ' (' + total + (po.supplier_name ? ', ' + po.supplier_name : '') + '): ' + st.count + ' of ' + st.required + ' approval' + (st.required > 1 ? 's' : '') +
    ', needs: ' + (needs.length ? needs.join(' + ') : 'nothing more') + ', you can approve: ' + (can ? 'yes' : 'no' + (already ? ' (you already approved)' : needs.length ? ' (your login cannot give this approval)' : ''))
  return { approvals: st.count + ' of ' + st.required, needs, you_can_approve: can, line }
}

// drafts waiting for this login
export function waitingFor(pos, approvalsByPo, roles, uid) {
  return (pos || []).filter(p => canApproveNow(p, (approvalsByPo || {})[p.id], roles, uid))
}

// compare table: one row per quote, cheapest marked (compared in the settings currency; complete quotes first)
export function compareQuotes(poLines, quotes, quoteLines, settings) {
  const s = { ...DEFAULT_PO_SETTINGS, ...(settings || {}) }
  const rate = c => String(c || s.currency).toUpperCase() === String(s.currency).toUpperCase() ? 1 : num((s.rates || {})[String(c).toUpperCase()])
  const rows = (quotes || []).filter(q => q.status !== 'requested' || (quoteLines || []).some(x => x.quote_id === q.id)).map(q => {
    const mine = (quoteLines || []).filter(x => x.quote_id === q.id)
    const per = (poLines || []).map(l => { const x = mine.find(m => m.po_line_id === l.id); const p = x ? num(x.unit_price) : null; return { line: l, unit_price: p, total: p != null ? Math.round(p * Number(l.qty) * 100) / 100 : null, note: x && x.note } })
    const missing = per.filter(x => x.unit_price == null)
    const total = per.some(x => x.total != null) ? Math.round(per.reduce((a, x) => a + (x.total || 0), 0) * 100) / 100 : null
    const r = rate(q.currency)
    return { quote: q, lines: per, total, currency: q.currency, missing: missing.map(x => x.line.description), complete: !missing.length, delivery_date: q.delivery_date || null, compare: total != null && r != null ? total * r : null }
  })
  const pool = rows.filter(r => r.complete && r.compare != null).length ? rows.filter(r => r.complete && r.compare != null) : rows.filter(r => r.compare != null)
  const best = pool.reduce((a, r) => (a == null || r.compare < a.compare ? r : a), null)
  rows.forEach(r => { r.cheapest = !!best && r === best })
  // cheapest per line (for marking cells)
  ;(poLines || []).forEach(l => {
    const priced = rows.map(r => ({ r, x: r.lines.find(y => y.line.id === l.id) })).filter(o => o.x && o.x.unit_price != null && rate(o.r.currency) != null)
    const lo = priced.reduce((a, o) => (a == null || o.x.unit_price * rate(o.r.currency) < a.x.unit_price * rate(a.r.currency) ? o : a), null)
    if (lo) lo.x.cheapest = true
  })
  return rows
}

// RFQ text for WhatsApp (no prices)
export function rfqText(po, lines, vessel, supplierName) {
  const v = vessel || {}
  const out = ['REQUEST FOR QUOTATION ' + (po.po_number || ''), (v.vessel_name || 'Vessel') + (supplierName ? ' → ' + supplierName : ''), '']
  ;(lines || []).forEach((l, i) => out.push((i + 1) + '. ' + fmtQty(l.qty) + ' ' + (l.unit || 'pcs') + ' × ' + l.description + (l.part_number ? ' (P/N ' + l.part_number + ')' : '')))
  out.push('')
  if (po.delivery_port) out.push('Deliver to: ' + po.delivery_port)
  if (po.needed_by) out.push('Needed by: ' + po.needed_by)
  out.push('', 'Please send your unit prices, delivery date and any notes, quoting ' + (po.po_number || 'this request') + '. This is a request for a quote, not an order.')
  return out.join('\n')
}

// wishlist -> orders: selected items grouped by the chosen supplier (null = no supplier yet)
export function wishlistGroups(items, choice) {
  const g = new Map()
  for (const it of items || []) {
    const sup = (choice && choice[it.id] !== undefined ? choice[it.id] : it.supplier_id) || null
    if (!g.has(sup)) g.set(sup, [])
    g.get(sup).push(it)
  }
  return [...g.entries()].map(([supplier_id, list]) => ({ supplier_id, items: list }))
}

// wishlist lines a login with Edit may delete: open, restocked, not needed (cancelled) - never one that is on an order
// (the database refuses that too: sql/test-fixes-4.sql)
export const WISH_DELETABLE = ['open', 'restocked', 'cancelled']
export const canDeleteWish = i => !!i && WISH_DELETABLE.includes(i.status)
